import Board from './Board';
import {
  NODE_COLOR_CLASS,
  TMT_GAME_NAME,
  type TmtStageConfig,
} from '@/pages/tmt-spick/tmtConfig';

interface GameScreenProps {
  config: TmtStageConfig;
  path: string[];
  wrongId: string | null;
  completed: boolean;
  remainingMs: number;
  transitionMsg: string | null;
  onSelect: (id: string) => void;
}

function formatTime(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// 스픽(Spick) 버전 게임 화면
// - 게임명 + 남은 시간 배지 헤더 밴드 (단계 표시 없음)
// - 전체 45초 타이머가 1~3단계에 걸쳐 하나로 이어진다.
export default function GameScreen({
  config,
  path,
  wrongId,
  completed,
  remainingMs,
  transitionMsg,
  onSelect,
}: GameScreenProps) {
  const hasCondition = !!config.ruleColor;
  const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
  const isLastTen = remainingSec <= 10;
  const isLastFive = remainingSec <= 5;

  return (
    <div className="relative flex flex-col h-full sh-game-bg pb-[var(--sh-screen-pb)] animate-fade-in-up">
      {/* 게임명 + 남은 시간 */}
      <div className="shrink-0 h-[44px] sh-header-bg px-5 flex items-center justify-between">
        <h1 className="text-[14px] font-bold text-foreground-950 whitespace-nowrap">
          {TMT_GAME_NAME}
        </h1>
        <span
          className={`inline-flex items-center h-7 px-3 rounded-full text-white text-[13px] font-bold tabular-nums whitespace-nowrap shrink-0 ${
            isLastTen ? 'bg-accent-500' : ''
          } ${isLastFive ? 'animate-timer-pulse' : ''}`}
          style={isLastTen ? undefined : { backgroundColor: 'var(--sh-stage-badge-bg)' }}
        >
          {formatTime(remainingMs)}
        </span>
      </div>

      {/* 규칙 안내 / 완성·전환 메시지: 게임보드와 겹치지 않게 안내 영역에 노출 */}
      <div className="h-[var(--sh-guidance-height)] shrink-0 flex items-center justify-center px-4 overflow-hidden">
        {completed ? (
          <span className="animate-pop-in inline-flex items-center h-8 px-4 rounded-full bg-primary-600 text-white text-[15px] font-extrabold whitespace-nowrap">
            {config.completeMsg}
          </span>
        ) : transitionMsg ? (
          <span className="animate-pop-in inline-flex items-center h-8 px-4 rounded-full bg-primary-600 text-white text-[14px] font-bold whitespace-nowrap">
            {transitionMsg}
          </span>
        ) : (
          <div className="flex items-center justify-center max-w-full">
            {hasCondition && (
              <span
                className={`inline-block w-3.5 h-3.5 shrink-0 ${
                  NODE_COLOR_CLASS[config.ruleColor!]
                } ${config.ruleShape === 'square' ? 'rounded-[3px]' : 'rounded-full'} mr-1.5`}
              />
            )}
            <p className="text-[15px] font-bold leading-snug text-center text-foreground-950">
              {config.ruleHint}
            </p>
          </div>
        )}
      </div>

      {/* 게임보드 */}
      <div className="flex-1 min-h-0 flex flex-col px-5">
        <div className="relative w-full flex-1 min-h-0 rounded-3xl overflow-hidden bg-white sh-dot-grid">
          <Board
            nodes={config.nodes}
            boardH={config.boardH}
            path={path}
            wrongId={wrongId}
            completed={completed}
            onSelect={onSelect}
            nodeSize={config.nodeSize}
          />
        </div>
      </div>
    </div>
  );
}